import { useState, useEffect } from 'react';
import { chargerService } from '../services/chargerService';
import { Session, QueueEntry } from '../types';

export interface UserActivity {
  isCharging: boolean;
  isInQueue: boolean;
  activeSession: Session | null;
  queueEntries: QueueEntry[];
}

export function useUserActivity() {
  const [activity, setActivity] = useState<UserActivity>({
    isCharging: false,
    isInQueue: false,
    activeSession: null,
    queueEntries: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const checkUserActivity = async (): Promise<UserActivity> => {
    try {
      setError(null);
      
      // Check for active charging session
      const [activeSession, queueEntries] = await Promise.all([
        chargerService.getCurrentSession().catch(() => null),
        chargerService.getMyQueueEntries().catch(() => [])
      ]);

      const newActivity: UserActivity = {
        isCharging: activeSession !== null,
        isInQueue: queueEntries.length > 0,
        activeSession,
        queueEntries
      };

      setActivity(newActivity);
      return newActivity;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to check user activity';
      setError(errorMessage);
      console.error('Error checking user activity:', err);
      
      // Return default state on error
      const defaultActivity: UserActivity = {
        isCharging: false,
        isInQueue: false,
        activeSession: null,
        queueEntries: []
      };
      setActivity(defaultActivity);
      return defaultActivity;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkUserActivity();
  }, []);

  return {
    activity,
    isLoading,
    error,
    refresh: checkUserActivity
  };
}